'use client';

import { ImageOff } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/ui-kit/utils';
import { giphyGifUrl } from './gif';

interface GifMessageProps {
  id: string;
  dimmed?: boolean;
}

// Renders a GIF comment. While the (variable-size) GIF loads, the <img> has no
// intrinsic dimensions and would collapse the bubble to nothing — so we hold a
// fixed placeholder box with a spinner until it loads, then size to the GIF.
export const GifMessage = ({ id, dimmed }: GifMessageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const pending = !loaded && !errored;

  return (
    <div
      className={cn(
        'relative self-start overflow-hidden rounded-[10px]',
        (pending || errored) && 'h-[140px] w-[180px]',
        pending && 'animate-pulse bg-v2-surface-secondary',
        errored && 'flex items-center justify-center bg-v2-surface-tertiary',
        dimmed && 'opacity-50',
      )}
    >
      {errored && <ImageOff className="size-5 text-v2-text-secondary" />}
      {!errored && (
        // biome-ignore lint/performance/noImgElement: external animated GIF of unknown size; next/image can't optimize it
        <img
          src={giphyGifUrl(id)}
          alt=""
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={cn(
            'block max-h-[200px] max-w-[280px] rounded-[10px]',
            !loaded && 'invisible absolute',
          )}
        />
      )}
    </div>
  );
};
